import debounce from "lodash/debounce";
import { useEffect, useMemo, useState } from "react";

/**
 * A hook that provides a debounced version of a value
 * @template T The type of the value to debounce
 * @param value The value to debounce
 * @param delay The delay in milliseconds
 * @returns The debounced value of the same type as the input
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  const debouncedSetter = useMemo(
    () =>
      debounce((newValue: T) => {
        setDebouncedValue(newValue);
      }, delay),
    [delay],
  );

  useEffect(() => {
    debouncedSetter(value);

    return () => {
      debouncedSetter.cancel();
    };
  }, [value, debouncedSetter]);

  return debouncedValue;
}
